import { Component, Emit, Mixins, Prop, Vue, Watch } from 'vue-property-decorator'
import { createPopper, Instance as PopperInstance } from '@popperjs/core'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { KFormInput } from '@kwixeo/form-vue'
import { BInputGroup, BInputGroupAppend, BButton, BListGroup, BListGroupItem, BModal, VBTooltip } from 'bootstrap-vue'
import { NumberMixin } from '@kwixeo/interface-vue'
import CatalogList from '../CatalogList/CatalogList.vue'
import { mapGetters } from 'vuex'

@Component({
  components: {
    CatalogList,
    FontAwesomeIcon,
    KFormInput,
    BInputGroup, BInputGroupAppend, BButton, BListGroup, BListGroupItem, BModal
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  computed: {
    ...mapGetters('subscriptions', ['hasModule']),
  }
})
export default class CatalogSearch extends Mixins(NumberMixin) {
  hasModule!: (key: string) => boolean

  @Prop({ default: null }) only!: string|string[]|null
  @Prop({ default: function () { return this.$kwCatalog.i18n.t('catalog.catalogSearch.placeholder') } }) placeholder!: string
  @Prop({ default: null }) include!: { [key: string]: string[] }|null
  @Prop({ default: null }) size!: string|null
  @Prop({ default: true }) withModal!: boolean
  @Prop({ default: null }) filters!: KwixeoInterface.GenericObject|null
  @Prop({ default: false }) disabled!: boolean

  private searchTerm: string|null = null
  private timeout: number|null = null
  private results: null|KwixeoInterface.SearchResult = null
  private loading = false
  private dropdownState = false
  private popperInstance: PopperInstance|null = null
  protected showCatalogModal = false

  get computedFilters (): KwixeoInterface.GenericObject {
    const filters: KwixeoInterface.GenericObject =  this.filters ?? {}
    filters.status = true
    return filters
  }

  @Watch('dropdownState')
  private watchDropdownState (state: boolean): void {
    if (state) {
      this.initDropdown()
    } else if (this.popperInstance) {
      this.popperInstance.destroy()
      this.results = null
    }
  }

  private initDropdown (): void {
    const input = this.$refs.input as Vue
    this.popperInstance = createPopper(
      input.$el,
      this.$refs.list as HTMLElement,
      {
        placement: 'bottom-start',
        modifiers: [
          {
            name: 'setWidth',
            enabled: true,
            fn: ({ state, instance }) => {
              const group = this.$refs.group as HTMLDivElement|undefined
              if (!group) {
                return
              }

              const rawWidth = group.clientWidth
              const minWidth = rawWidth + 'px'
              if (minWidth === state.styles.popper.minWidth) {
                return
              }

              state.styles.popper.minWidth = minWidth

              const maxWidth = window.innerWidth - group.getBoundingClientRect().x - 30
              if (maxWidth > (rawWidth + 300)) {
                state.styles.popper.width = (maxWidth - 300) + 'px'
              }

              instance.update()
            },
            phase: 'beforeWrite'
          },
        ],
      }
    )
  }

  private onInput (val: string): void {
    if (this.timeout) {
      clearTimeout(this.timeout)
    }

    if (!val) {
      this.dropdownState = false
      this.loading = false
      return
    }

    this.results = null
    this.dropdownState = true
    this.loading = true
    this.timeout = setTimeout(this.search, 400)
  }

  private clearSearch (): void {
    this.results = null
    this.dropdownState = false
    this.loading = false
    this.searchTerm = null
  }

  private async search (): Promise<void> {
    if (!this.searchTerm) {
      this.timeout = null
      this.loading = false
      return
    }

    const params: KwixeoInterface.GenericObject = {
      total: 10,
      search: this.searchTerm,
      filters: this.computedFilters
    }

    if (this.include) {
      params.include = this.include
    }

    if (this.only) {
      params.elements = Array.isArray(this.only) ? this.only : [this.only]
    }

    await this.$kwCatalog.axios.get('/catalog/search', {
      params
    })
      .then(response => {
        this.results = response.data
      })
      .catch(error => {
        throw new Error(error)
      })
      .finally(() => {
        this.timeout = null
        this.loading = false
      })
  }

  private selectEntry (entry: KwixeoInterface.SearchResultData): void {
    this.clearSearch()
    this.selectedEntry(JSON.parse(JSON.stringify(entry)))
  }

  @Emit('selected')
  private selectedEntry (entry: KwixeoInterface.SearchResultData): KwixeoInterface.SearchResultData {
    return entry
  }
}
